import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { suggestOpportunity } from "../services/parseService";

interface SuggestOpportunityFormProps {
  isVisible: boolean;
  onClose: () => void;
}

const SuggestOpportunityForm: React.FC<SuggestOpportunityFormProps> = ({
  isVisible,
  onClose,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [scaleAnim] = useState(new Animated.Value(0));

  useEffect(() => {
    if (showSuccess) {
      Animated.spring(scaleAnim, {
        toValue: 1,
        useNativeDriver: true,
        tension: 100,
        friction: 8,
      }).start();
    }
  }, [showSuccess, scaleAnim]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setLink("");
    setContactEmail("");
    setShowSuccess(false);
    scaleAnim.setValue(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (title.trim().length < 3) {
      Alert.alert("Error", "Please enter a title for the opportunity");
      return;
    }
    if (description.trim().length < 10) {
      Alert.alert("Error", "Please provide a detailed description");
      return;
    }
    try {
      setIsSubmitting(true);
      await suggestOpportunity({
        title: title.trim(),
        description: description.trim(),
        contactEmail: contactEmail.trim() || undefined,
        link: link.trim() || undefined,
      });
      
      // Show success animation
      setIsSubmitting(false);
      setShowSuccess(true);
    } catch (error) {
      Alert.alert("Error", "Failed to submit suggestion.");
      console.error("Error submitting suggestion:", error);
      setIsSubmitting(false);
    }
  };

  const SuccessScreen = () => (
    <View style={styles.successContainer}>
      <Animated.View 
        style={[
          styles.successContent,
          { transform: [{ scale: scaleAnim }] }
        ]}
      >
        <View style={styles.checkmarkContainer}>
          <Ionicons name="checkmark-circle" size={64} color="#10B981" />
        </View>
        <Text style={styles.successTitle}>Suggestion Submitted!</Text>
        <Text style={styles.successMessage}>
          Thank you for your suggestion! We'll review it and consider adding it to our opportunities database.
        </Text>
        <TouchableOpacity
          style={styles.continueButton}
          onPress={handleClose}
        >
          <Text style={styles.continueButtonText}>Continue</Text>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
      data-oid="4enezj."
    >
      <SafeAreaView style={styles.safeArea} data-oid="rpaqp09">
        <View style={styles.container} data-oid="r2owp5h">
          {showSuccess ? (
            <SuccessScreen />
          ) : (
            <>
              <View style={styles.header} data-oid="vunijxl">
                <Text style={styles.title} data-oid="f35s1c7">
                  Suggest an Opportunity
                </Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={handleClose}
                  data-oid="q4vptqx"
                >
                  <Ionicons
                    name="close"
                    size={24}
                    color="#4A5568"
                    data-oid="p6zll8x"
                  />
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.content} data-oid="3hqyb:w">
                <Text style={styles.inputLabel} data-oid="k.n.ty7">
                  Opportunity Title:
                </Text>
                <TextInput
                  style={styles.textInput}
                  value={title}
                  onChangeText={setTitle}
                  placeholder="Opportunity name"
                  data-oid="hbmq8t."
                />

                <Text style={styles.inputLabel} data-oid="x9_34:9">
                  Description:
                </Text>
                <TextInput
                  style={styles.textArea}
                  multiline
                  numberOfLines={6}
                  textAlignVertical="top"
                  value={description}
                  onChangeText={setDescription}
                  placeholder="Tell us about this opportunity"
                  data-oid="hzl337n"
                />

                <Text style={styles.inputLabel} data-oid="hd:a5d3">
                  Link (optional):
                </Text>
                <TextInput
                  style={styles.textInput}
                  value={link}
                  onChangeText={setLink}
                  placeholder="https://example.com"
                  data-oid="ll_6lxn"
                />

                <Text style={styles.inputLabel} data-oid="681f:-3">
                  Your email (optional):
                </Text>
                <TextInput
                  style={styles.textInput}
                  keyboardType="email-address"
                  value={contactEmail}
                  onChangeText={setContactEmail}
                  placeholder="email@example.com"
                  data-oid="ql74xpz"
                />

                <TouchableOpacity
                  style={[
                    styles.submitButton,
                    isSubmitting && styles.submitButtonDisabled,
                  ]}
                  onPress={handleSubmit}
                  disabled={isSubmitting}
                  data-oid="w2lppkd"
                >
                  {isSubmitting ? (
                    <ActivityIndicator
                      size="small"
                      color="#FFFFFF"
                      data-oid="09-g510"
                    />
                  ) : (
                    <>
                      <Ionicons
                        name="paper-plane-outline"
                        size={18}
                        color="#fff"
                        data-oid="yq.4bcu"
                      />

                      <Text style={styles.submitButtonText} data-oid="4a1.e6b">
                        Submit Suggestion
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </ScrollView>
            </>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    width: "90%",
    maxWidth: 500,
    maxHeight: "90%",
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    color: "#2D3748",
    fontFamily: "Inter-Bold",
  },
  closeButton: {
    padding: 8,
    borderRadius: 16,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 16,
    color: "#4A5568",
    fontFamily: "Inter-Regular",
  },
  textArea: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    fontSize: 16,
    color: "#4A5568",
    minHeight: 100,
    fontFamily: "Inter-Regular",
  },
  submitButton: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#272727",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
    marginLeft: 8,
    fontFamily: "Inter-SemiBold",
  },
  successContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  successContent: {
    alignItems: "center",
    width: "100%",
  },
  checkmarkContainer: {
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#2D3748",
    fontFamily: "Inter-Bold",
    marginBottom: 12,
    textAlign: "center",
  },
  successMessage: {
    fontSize: 16,
    color: "#4A5568",
    fontFamily: "Inter-Regular",
    textAlign: "center",
    lineHeight: 24,
    marginBottom: 32,
  },
  continueButton: {
    backgroundColor: "#10B981",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 32,
    minWidth: 120,
  },
  continueButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    textAlign: "center",
  },
});

export default SuggestOpportunityForm;
